import { clearCallback, linkauthLinks, readCallback } from "@linky-fit/linkauth/client"
import { toDataURL } from "qrcode"
import { watchVersion } from "./lib/version"

type LoginResult = { status: "ok" | "pending" } | { status: "error"; reason: string }

const root = document.querySelector<HTMLElement>("[data-login]")!
const find = <T extends HTMLElement>(selector: string) => root.querySelector<T>(selector)!
const audience = root.dataset.publicUrl ?? location.origin
const openLink = find<HTMLAnchorElement>("[data-open]")
const qr = find<HTMLImageElement>("[data-qr]")
const errorText = find("[data-error]")

/**
 * An iOS home-screen app keeps its own cookies, apart from Safari. A same-device Linky login returns through the
 * callback page, which iOS opens in Safari, so the session would land there. Inside the installed app the login
 * travels over the relays instead and this page picks it up by polling.
 */
const standalone = matchMedia("(display-mode: standalone)").matches || ("standalone" in navigator && navigator.standalone === true)

const ERRORS: Record<string, string> = {
  expired: "The login expired. Try again.",
  not_admin: "This Nostr key may not use the dashboard.",
  denied: "Login cancelled in Linky.",
}
const showError = (reason: string) => {
  errorText.textContent = ERRORS[reason] ?? "The login did not go through. Try again."
  errorText.hidden = false
}

const post = async (path: string, body?: unknown): Promise<LoginResult> => {
  const response = await fetch(path, {
    method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(body ?? {}),
  })
  return response.json() as Promise<LoginResult>
}

let starting: Promise<void> | undefined
/** Every way of logging in on this page signs the nonce issued here. */
const start = () => starting ??= (async () => {
  const { nonce } = await (await fetch("/auth/start", { method: "POST", credentials: "same-origin" })).json() as { nonce: string }
  const { openUrl, qrUrl } = linkauthLinks({ audience, nonce, callbackUrl: `${audience}/login`, signerApp: "nightly" })
  openLink.href = standalone ? qrUrl : openUrl
  qr.src = await toDataURL(qrUrl, { width: 320, margin: 4, errorCorrectionLevel: "M" })
  openLink.hidden = false
  qr.hidden = false
  find("[data-waiting]").hidden = false
  if (standalone) {
    find("[data-hint]").textContent = "Open Linky, approve the login, then come back to this app. It logs in by itself."
  }
})().finally(() => { starting = undefined })

/** Only local paths, so a crafted link cannot send a fresh session elsewhere. */
const next = new URLSearchParams(location.search).get("next")
const home = next?.startsWith("/") && !next.startsWith("//") ? next : "/"

/** A failed attempt used up the nonce, so a retry needs a new one. */
const finish = async (result: LoginResult) => {
  if (result.status === "ok") return location.assign(home)
  if (result.status === "error") {
    showError(result.reason)
    await start()
  }
}

let polling = false
const pollOnce = async () => {
  if (polling || document.hidden) return
  polling = true
  try {
    const result = await post("/auth/poll").catch((): LoginResult => ({ status: "pending" }))
    if (result.status === "error" && result.reason === "expired") await start()
    else await finish(result)
  } finally {
    polling = false
  }
}

const callback = readCallback(location.hash)
if (callback) clearCallback()
if (callback && "assertion" in callback) await finish(await post("/auth/finish", callback.assertion))
else {
  if (callback) showError(callback.error)
  await start()
}
setInterval(pollOnce, 2000)
watchVersion()
// Returning from Linky should not wait for the next tick.
document.addEventListener("visibilitychange", () => void pollOnce())
